import { observable } from 'rewire-core';

export interface IPaging {
  search?: string;
  readonly page: number;
  readonly count: number;
  itemsPerPage: number;
  readonly currentPage?: string;
  readonly loading: boolean;

  next(): Promise<void>;
  previous(): Promise<void>;
}

export interface IPagingState {
  loading: boolean;
  search?: string;
  itemsPerPage: number;
  count: number;
  page: number;
}

export default class PagingModel implements IPaging {
  private _state: IPagingState;
  private _pageInfo: { next?: string; previous?: string }[];
  currentPage?: string;

  public constructor(
    private _load: (filter?: { search?: string }) => Promise<{ cursor?: string; totalCount: number }>,
    itemsPerPage = 50,
  ) {
    this._pageInfo = [];
    this._pageInfo[0] = {};
    this._state = observable({
      page: 0,
      count: 0,
      itemsPerPage: itemsPerPage,
      loading: false,
    });
  }

  async load(internalLoad = false): Promise<void> {
    const filter: any = {};
    if (this.search) {
      filter.search = this.search;
    }
    if (!internalLoad) {
      // a 'new' load is being called externally (vs. an internal load caused by next() / previous() / search())
      // reset the currentPage so server filtering is applied and includes the new batches as well (i.e. those with 0 timecards)
      this.currentPage = undefined;
      this._state.page = 0;
    }

    try {
      this._state.loading = true;
      const result: { cursor?: string; totalCount: number } = await this._load(filter);
      this._pageInfo[this._state.page].next = result.cursor;
      this._state.count = result.totalCount;
    } finally {
      this._state.loading = false;
    }
  }

  get loading() {
    return this._state.loading;
  }

  set loading(loading: boolean) {
    this._state.loading = loading;
  }

  get search(): string | undefined {
    return this._state.search;
  }

  set search(value: string | undefined) {
    this._state.search = value;
    this._state.page = 0;
    this.currentPage = undefined;
    this.load(true);
  }

  get page(): number {
    return this._state.page;
  }

  get count(): number {
    return this._state.count;
  }

  get itemsPerPage(): number {
    return this._state.itemsPerPage;
  }

  set itemsPerPage(value: number) {
    if (value === this._state.itemsPerPage) return;
    this._state.itemsPerPage = value;
    this.load();
  }

  async next(): Promise<void> {
    if (this._state.loading) return;
    const info = this._pageInfo[this._state.page];
    const previous = this.currentPage;
    this.currentPage = info.next;
    this._state.page++;
    this._pageInfo[this._state.page] = { previous };
    this.load(true);
  }

  async previous(): Promise<void> {
    if (this._state.loading) return;
    if (this._state.page === 0) return;
    this.currentPage = this._pageInfo[this._state.page].previous;
    this._state.page--;
    this.load(true);
  }
}
